import { merge, of, Subject } from 'rxjs';
import { catchError, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';

import { SortParameters } from '@lib/api';
import { SnAudit } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';

import { SnLpr } from '../../api';

@Component({
  templateUrl: './lpr-audit-dialog.component.html',
  styleUrls: ['./lpr-audit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LprAuditDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) public paginator: MatPaginator;
  @ViewChild(MatSort) public matSort: MatSort;
  @ViewChild(MatTable) public matTable: any;

  public get columns(): string[] {
    return this._getColumns();
  }

  public sort: string = 'createdAt';
  public sortDirection: SortDirection = 'asc';
  public resultsLength = 0;
  public pageSize: number = 10;
  public dataSource: MatTableDataSource<SnAudit>;
  public isLoading: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public lpr: SnLpr,
    private readonly _auditAPI: SnAuditService,
  ) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public ngAfterViewInit(): void {
    this._fetchData();
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;
  }

  public onSearchValueChange(filterValue: string): void {
    if (filterValue) {
      filterValue = filterValue.trim();
      filterValue = filterValue.toLowerCase();
      this.dataSource.filter = filterValue;
    } else {
      this.dataSource.filter = '';
    }
  }

  public reload(): void {
    this._fetchData();
  }

  public audit(): void {
    window.open('/audit?content_type__in=LprHotlistObject%2cLprHotlistObjectGroup', '_blank');
  }

  private _fetchData(): void {
    this.isLoading = true;
    merge(this.matSort.sortChange)
      .pipe(
        startWith({}),
        switchMap(() => {
          return this._auditAPI
            .getAuditList({
              ordering: new SortParameters({
                active: this.sort,
                direction: this.sortDirection,
              }).convertedActive,
              content_type__in: 'LprHotlistObject,LprHotlistObjectGroup',
            })
            .pipe(catchError(() => of(null)));
        }),
        map((data) => {
          if (data === null) {
            return [];
          }
          this.resultsLength = data.count;
          return data;
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((res) => {
        this.dataSource = new MatTableDataSource(res.results);
        this.dataSource.paginator = this.paginator;
        this.dataSource.filterPredicate = (data: any, filter) => {
          const dataStr = JSON.stringify(data).toLowerCase();
          return dataStr.indexOf(filter) !== -1;
        };
        this.isLoading = false;
      });
  }

  private _getColumns(): string[] {
    return ['user', 'object', 'action', 'createdAt'];
  }
}
